import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CommonModal, createModalHook, ModalOptions } from '@/entities/modal';
import { useMatchMedia } from '@/shared/lib/hooks/use-match-media.hook';
import { Box } from '@/shared/ui/box';
import { Stepper } from '@/shared/ui/stepper';
import { Text } from '@/shared/ui/text';

import { Steps } from '../../model/utils/agreements.util';
import { useAgreementsLogic } from '../../model/utils/hooks/use-agreements-logic.hook';
import { CheckboxList } from '../checkbox-list';
import { PdfToImageViewer } from '../pdf-to-image-viewer';
import { ScrollableBox } from './agreements.styles';

const AgreementsModelComponent = memo(({ onClose }: ModalOptions) => {
	const { t } = useTranslation('agreementsSettings');

	const {
		isLoading,
		isFullscreen,
		steps,
		sizeText,
		currentStep,
		isFirstAcceptance,
		checkboxState,
		handleCheckboxChange,
		nextStep,
		isAllTrue,
	} = useAgreementsLogic(onClose);

	const isMobile = useMatchMedia('mobile');
	const isLaptop = useMatchMedia('tabletAndLaptop');
	const sizeTitle = isFullscreen ? 'l' : isMobile ? 's' : 'm';
	const isConfirmDisabled =
		currentStep.title === Steps.TermsAndConditions
			? !isAllTrue(checkboxState[Steps.TermsAndConditions])
			: !(isAllTrue(checkboxState[Steps.TermsAndConditions]) && isAllTrue(checkboxState[Steps.PrivacyPolicy]));

	useEffect(() => {
		const panels = Array.from(document.getElementsByClassName('panel'));
		const lastPanel = Array.from(document.getElementsByClassName('panel'))[panels.length - 1] as
			| HTMLElement
			| undefined;

		if (lastPanel) {
			lastPanel.style.width = isLaptop ? '81%' : '60%';
		}
	}, [isLaptop]);

	return (
		<CommonModal
			title={isFirstAcceptance ? t('modal.first.acceptance.agreement.title') : t('modal.agreement.title')}
			sizeTitle={sizeTitle}
			onClose={onClose}
			allowClose={false}
			fullscreen={!isMobile}
			onConfirm={nextStep}
			okText="Confirm"
			width="100%"
			isLoading={isLoading}
			disabled={isConfirmDisabled}
		>
			<Box direction="column" gap={isMobile ? 12 : 24} width="100%">
				<Stepper steps={steps.map((step) => step.title)} currentStep={currentStep.title} />
				<Box direction="column" gap={8}>
					<Text
						color="secondary"
						weight={600}
						size={sizeTitle}
						label={isFirstAcceptance ? currentStep.caption : currentStep?.lastAcceptedCaption || currentStep.caption}
					/>
					<Text
						size={sizeText}
						label={isFirstAcceptance ? currentStep.text : currentStep?.lastAcceptedText || currentStep.text}
					/>
				</Box>
				<ScrollableBox direction="column" gap={12} fullscreen={isFullscreen} isMobile={isMobile}>
					{currentStep.file && (
						<PdfToImageViewer key={currentStep.id} pdfUrl={currentStep.file} fullscreen={isFullscreen} />
					)}
					<CheckboxList
						checkboxes={currentStep.checkboxes}
						checkedState={checkboxState[currentStep.title]}
						onChange={(index) => handleCheckboxChange(currentStep.title, index)}
						sizeText={sizeText}
					/>
				</ScrollableBox>
			</Box>
		</CommonModal>
	);
});

export default createModalHook<ModalOptions>((props) => () => <AgreementsModelComponent {...props} />);
